import './AvailableClubsTabsExpertMode.styles.scss';

import { observer } from 'mobx-react';
import { Dialog } from 'primereact/dialog';
import { Tooltip } from 'primereact/tooltip';
import React, { FC, useEffect, useMemo, useState } from 'react';

import { getPokerRoom } from '../../../constants';
import { AvailableBotResponse } from '../../../model/availablebot/AvailableBotResponse';
import { Permission } from '../../../model/enumeration/Permission.enum';
import { PokerRoom } from '../../../model/enumeration/PokerRoom';
import ManageBotSeating from '../../../pages/Dashboard/ManageBotSeating/ManageBotSeating';
import authStore from '../../../store/AuthStore';
import availableBotStore from '../../../store/AvailableBotStore';
import expertModeStore from '../../../store/ExpertModeStore';
import notificationStore from '../../../store/NotificationStore';
import permissionStore from '../../../store/PermissionStore';
import AvailableBotsForSitWithClub from '../../AvailableBotsForSitWithClub/AvailableBotsForSitWithClub';

const AvailableClubsTabsExpertMode: FC = () => {
  const [selectedAvailableBot, setSelectedAvailableBot] =
    useState<AvailableBotResponse>();

  const [selectedClub, setSelectedClub] = useState<string>(null);
  const [isManageSeatingPanelOpen, setManageSeatingPanelOpen] = useState(false);

  const isClubGG = getPokerRoom() === PokerRoom.ClubGG;

  const hasManageBotSeatingPermission = useMemo(
    () => permissionStore.hasPermission(Permission.MANAGE_BOTS_SEATING),
    [authStore.authenticatedUser],
  );

  const hasChangeBotsClubsPermission = useMemo(
    () => permissionStore.hasPermission(Permission.CHANGE_BOTS_CLUBS),
    [authStore.authenticatedUser],
  );

  useEffect(() => {
    const botId = expertModeStore.getSelectedBot()?.pid;
    if (botId) {
      availableBotStore
        .getAvailableBot(botId)
        .then(bot => {
          setSelectedAvailableBot(bot);
          setSelectedClub(bot.club);
        })
        .catch(() => setSelectedAvailableBot(null));
    } else {
      setSelectedAvailableBot(null);
      setSelectedClub(null);
      setManageSeatingPanelOpen(false);
    }
  }, [expertModeStore.selectedBot]);

  const onSelectClubTab = (club: string): void => {
    if (selectedAvailableBot.club === club) {
      setSelectedClub(club);
      setManageSeatingPanelOpen(true);
    } else {
      availableBotStore
        .updateClub(selectedAvailableBot.pid, selectedAvailableBot.room, club)
        .then(() => {
          setSelectedClub(club);
          setManageSeatingPanelOpen(true);
          notificationStore.showNotification({
            severity: 'success',
            summary: 'Success',
            detail: (
              <>
                Request sent to the bot (<b>{selectedAvailableBot.pid}</b>) to
                change the club to <b>{club}</b>
              </>
            ),
          });
        })
        .catch(() =>
          notificationStore.showNotification({
            severity: 'error',
            summary: 'Error',
            detail: (
              <>
                Error while sending request to the bot (
                <b>{selectedAvailableBot.pid}</b>) to change the club to{' '}
                <b>{club}</b>
              </>
            ),
          }),
        );
    }
  };

  const onHide = (): void => setManageSeatingPanelOpen(false);

  const footer = useMemo(
    () =>
      hasManageBotSeatingPermission &&
      selectedClub?.length > 0 &&
      isManageSeatingPanelOpen &&
      hasChangeBotsClubsPermission && (
        <AvailableBotsForSitWithClub
          selectedClubs={[selectedClub]}
          excludedPids={[]}
        />
      ),
    [
      selectedClub,
      selectedClub?.length,
      isManageSeatingPanelOpen,
      hasManageBotSeatingPermission,
      hasChangeBotsClubsPermission,
    ],
  );

  return (
    <div
      className={`AvailableClubsTabsExpertMode-Component${
        isClubGG ? ' pinned' : ''
      }`}
    >
      {selectedAvailableBot && selectedAvailableBot.availableClubs && (
        <>
          {selectedAvailableBot.availableClubs.map(club => (
            <div key={`club-tab-${selectedAvailableBot.pid}-${club}`}>
              {club !== selectedClub && (
                <Tooltip
                  target={`#club-tab-${selectedAvailableBot.pid}-${club}`}
                />
              )}
              <div
                id={`club-tab-${selectedAvailableBot.pid}-${club}`}
                className={`club-tab${
                  club === selectedClub ? ' active-club-tab' : ''
                }`}
                onClick={() => onSelectClubTab(club)}
                data-pr-tooltip="Change club"
                data-pr-position="bottom"
              >
                {club}
              </div>
            </div>
          ))}
          {hasManageBotSeatingPermission && selectedClub?.length > 0 && (
            <Dialog
              key={`${selectedAvailableBot.pid}-manage-seating-dialog`}
              onHide={onHide}
              className="mt-2 AvailableClubsTabsExpertMode-Dialog"
              style={{
                height: `${
                  (expertModeStore.getContainerHeightCoefficient() + 0.1) * 100
                }%`,
              }}
              visible={isManageSeatingPanelOpen}
              header={false}
              footer={footer}
              dismissableMask
            >
              <ManageBotSeating selectedClub={selectedClub} />
            </Dialog>
          )}
        </>
      )}
    </div>
  );
};

export default observer(AvailableClubsTabsExpertMode);
